import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  CircularProgress,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import { useDispatch, useSelector } from "react-redux";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { fetchEmpAllocation } from "../../action/EmpAllocationAction";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const BRAND = "#330066" ;

// ─── Styled shells ────────────────────────────────────────────────────────────
const Page = styled(Box)(({ theme }) => ({
  width: "100%",
  padding: "0 0 80px",   
}));

const Card = styled(Box)({
  background: "#fff",
  border: "0.5px solid #e2e8f0",
  borderRadius: 12,
  overflow: "hidden",
});

const SectionLabel = styled(Typography)({
  fontSize: 11,
  fontWeight: 600,
  color: "#94a3b8",
  textTransform: "uppercase",
  letterSpacing: "0.6px",
  marginBottom: 6,
  marginTop: 14,
});

export default function EmployeeAllocationMain() {
  const dispatch = useDispatch();
  const { loading, data } = useSelector((state) => state.empAllocation);

  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("allocation");

  const formatDate = (d) => new Date(d).toISOString().split("T")[0];
  const formatTime = (d) =>
    new Date(d).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

  useEffect(() => {
    const today = new Date();
    setSelectedDate(today);
    dispatch(fetchEmpAllocation(formatDate(today)));
  }, [dispatch]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setOpenDatePicker(false);
    dispatch(fetchEmpAllocation(formatDate(date)));
  };

  // ── Filtered & grouped data ───────────────────────────────────────────────
  const filteredData =
    data?.filter((item) =>
      Object.values(item).join(" ").toLowerCase().includes(searchQuery.toLowerCase())
    ) || [];

  const groupedByTask = filteredData.reduce((acc, item) => {
    const task = item.CDLjob;
    if (!acc[task]) acc[task] = { CDLjob: task, countIWOno: 0 };
    acc[task].countIWOno += Number(item.countIWOno || 0);
    return acc;
  }, {});

  const groupedBySupervisor = filteredData.reduce((acc, item) => {
    const sup = item.Supervisorname;
    if (!acc[sup]) acc[sup] = { Supervisorname: sup, LocCode: item.LocCode, countIWOno: 0 };
    acc[sup].countIWOno += Number(item.countIWOno || 0);
    return acc;
  }, {});

  const taskArray = Object.values(groupedByTask).sort((a, b) => {
    const extract = (job) => (job || "").split("/")[0].trim().toUpperCase();
    const priority = ["NC", "SR", "OR"];
    const ai = priority.indexOf(extract(a.CDLjob));
    const bi = priority.indexOf(extract(b.CDLjob));
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return a.CDLjob.localeCompare(b.CDLjob);
  });

  const supervisorArray = Object.values(groupedBySupervisor);

  const totalEmployees = filteredData.reduce((s, i) => s + Number(i.countIWOno || 0), 0);

  const currentData =
    viewMode === "allocation" ? taskArray : viewMode === "supervisor" ? supervisorArray : filteredData;

  const colCount = viewMode === "supervisor" ? 3 : 2;

  const VIEW_LABELS = {
    allocation: "Summary – CDL Jobs",
    supervisor: "Summary – Supervisor",
    details: "Details",
  };

  // ── PDF export ────────────────────────────────────────────────────────────
  const handleExportPDF = () => {
    const doc = new jsPDF("p", "mm", "a4");
    const at = new Date();
    const pw = doc.internal.pageSize.getWidth();

    const title =
      viewMode === "allocation"
        ? "Employee Allocation – CDL Jobs Summary"
        : viewMode === "supervisor"
        ? "Employee Allocation – Supervisor Summary"
        : "Employee Allocation – Details";

    doc.setFontSize(22).setFont("helvetica", "bold").setTextColor(30, 58, 110);
    doc.text("DTS Biztrack", pw / 2, 20, { align: "center" });
    doc.setFontSize(13).setTextColor(100, 116, 139);
    doc.text(title, pw / 2, 30, { align: "center" });
    doc.setDrawColor(30, 58, 110).setLineWidth(0.5).line(15, 36, pw - 15, 36);

    doc.setFontSize(12).setFont("helvetica", "normal").setTextColor(60, 60, 60);
    const y = 44, sx = 15, ex = pw - 15;
    [
      [sx, "Date – ", formatDate(selectedDate)],
      [pw / 2, "Time – ", formatTime(at)],
      [ex, "Total – ", totalEmployees.toString()],
    ].forEach(([anchor, bold, val], i) => {
      const fw = doc.getTextWidth(bold + val);
      const x = i === 0 ? anchor : i === 2 ? anchor - fw : anchor - fw / 2;
      doc.setFont("helvetica", "bold").text(bold, x, y);
      doc.setFont("helvetica", "normal").text(val, x + doc.getTextWidth(bold), y);
    });

    let columns, rows;
    if (viewMode === "allocation") {
      columns = ["CDL Jobs", "Employees"];
      rows = taskArray.map((r) => [r.CDLjob || "", r.countIWOno || "0"]);
      rows.push(["Total", totalEmployees.toString()]);
    } else if (viewMode === "supervisor") {
      columns = ["Location", "Supervisor", "Total Employees"];
      rows = supervisorArray.map((r) => [r.LocCode || "", r.Supervisorname || "", r.countIWOno || "0"]);
      rows.push(["Total", "", totalEmployees.toString()]);
    } else {
      columns = ["Description", "No. of Employees"];
      rows = filteredData.map((r) => [r.EngSpec || "", r.countIWOno || "0"]);
      rows.push(["Total", totalEmployees.toString()]);
    }

    autoTable(doc, {
      head: [columns],
      body: rows,
      startY: 52,
      styles: { fontSize: 12, cellPadding: 5 },
      headStyles: { fillColor: [21, 76, 121], textColor: 255, fontStyle: "bold", halign: "center" },
      alternateRowStyles: { fillColor: [248, 249, 252] },
      columnStyles:
        viewMode === "supervisor"
          ? { 0: { cellWidth: 50, halign: "center" }, 1: { cellWidth: 97 }, 2: { cellWidth: 40, halign: "center" } }
          : { 0: { cellWidth: 147 }, 1: { cellWidth: 40, halign: "center" } },
      theme: "grid",
      margin: { left: 12, right: 10 },
      didParseCell(data) {
        if (data.row.index === rows.length - 1) {
          data.cell.styles.fillColor = [21, 76, 121];
          data.cell.styles.textColor = 255;
          data.cell.styles.fontStyle = "bold";
        }
      },
      didDrawPage(data) {
        doc.setFontSize(8).setTextColor(150).setFont("helvetica", "italic");
        doc.text(`Generated on ${formatDate(at)} at ${formatTime(at)}`, pw / 2, doc.internal.pageSize.getHeight() - 10, { align: "center" });
        doc.text(`Page ${data.pageNumber}`, pw - 20, doc.internal.pageSize.getHeight() - 10);
      },
    });

    doc.save(`Employee_Allocation_${viewMode}_${formatDate(selectedDate)}.pdf`);
  };

  // ─── Table header columns per view ────────────────────────────────────────
  const headers = {
    allocation: [
      { label: "CDL Jobs", align: "left", w: "75%" },
      { label: "Employees", align: "center", w: "25%" },
    ],
    supervisor: [
      { label: "Location", align: "left", w: "22%" },
      { label: "Supervisor", align: "left", w: "55%" },
      { label: "Total", align: "center", w: "23%" },
    ],
    details: [
      { label: "Description", align: "left", w: "75%" },
      { label: "Employees", align: "center", w: "25%" },
    ],
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Page>
        {/* ── Filter row ── */}
        <Box sx={{ display: "flex", gap: 1, mb: 1.5, alignItems: "center" }}>
          {/* View mode select */}
          <FormControl sx={{ flex: 1, minWidth: 0 }} size="small">
            <Select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
              sx={{
                height: 36, borderRadius: "20px", background: "#fff",
                fontSize: 12, fontWeight: 500, color: "#1e293b",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BRAND },
                "& .MuiSelect-select": { py: 0 },
              }}
            >
              <MenuItem value="allocation" sx={{ fontSize: 13 }}>Summary – CDL Jobs</MenuItem>
              <MenuItem value="supervisor" sx={{ fontSize: 13 }}>Summary – Supervisor</MenuItem>
              <MenuItem value="details" sx={{ fontSize: 13 }}>Details</MenuItem>
            </Select>
          </FormControl>

          {/* Date picker trigger */}
          <Box
            onClick={() => setOpenDatePicker(true)}
            sx={{
              height: 36, borderRadius: "20px", border: "0.5px solid #e2e8f0",
              background: "#fff", display: "flex", alignItems: "center",
              gap: "6px", px: "12px", cursor: "pointer", flexShrink: 0,
              "&:hover": { borderColor: BRAND },
            }}
          >
            <CalendarTodayIcon sx={{ fontSize: 14, color: "#64748b" }} />
            <Typography sx={{ fontSize: 12, color: "#1e293b", fontWeight: 500 }}>
              {formatDate(selectedDate)}
            </Typography>
          </Box>

          {openDatePicker && (
            <DatePicker
              open={openDatePicker}
              value={selectedDate}
              onChange={handleDateChange}
              onClose={() => setOpenDatePicker(false)}
              renderInput={(params) => <TextField {...params} sx={{ display: "none" }} />}
            />
          )}
        </Box>

        {/* ── Stats + PDF row ── */}
        {!loading && data?.length > 0 && (
          <Box sx={{ display: "flex", gap: 1, mb: 1.5, alignItems: "stretch" }}>
            {/* Total employees card */}
            <Box
              sx={{
                flex: 1, background: "#fff", border: "0.5px solid #e2e8f0",
                borderRadius: "12px", p: "10px 12px",
              }}
            >
              <Typography sx={{ fontSize: 11, color: "#64748b", mb: 0.5 }}>Total Employees</Typography>
              <Typography sx={{ fontSize: 22, fontWeight: 500, color: BRAND, lineHeight: 1.1 }}>
                {totalEmployees}
              </Typography>
            </Box>

            {/* PDF export button */}
            <Button
              size="small"
              startIcon={<DownloadIcon sx={{ fontSize: "14px !important" }} />}
              onClick={handleExportPDF}
              disabled={!data?.length}
              sx={{
                height: "auto", minHeight: 56, borderRadius: "12px",
                background: "#16a34a", color: "#fff", fontSize: 12,
                fontWeight: 500, textTransform: "none", px: 2,
                flexShrink: 0, flexDirection: "column", gap: "2px",
                "&:hover": { background: "#15803d" },
                "&:disabled": { background: "#cbd5e1", color: "#64748b" },
              }}
            >
              Export PDF
            </Button>
          </Box>
        )}

        {/* ── Search ── */}
        <Box sx={{ mb: 1.5 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={
              viewMode === "allocation"
                ? "Search CDL jobs…"
                : viewMode === "supervisor"
                ? "Search supervisors…"
                : "Search details…"
            }
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "20px",
                height: 36,
                fontSize: 13,
                background: "#fff",
                "& fieldset": { borderColor: "#e2e8f0" },
                "&:hover fieldset": { borderColor: BRAND },
                "&.Mui-focused fieldset": { borderColor: BRAND },
              },
            }}
          />
        </Box>

        {/* ── View label ── */}
        <SectionLabel>{VIEW_LABELS[viewMode]}</SectionLabel>

        {/* ── Table ── */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={28} sx={{ color: BRAND }} />
          </Box>
        ) : (
          <Card>
            <TableContainer sx={{ maxHeight: "calc(100vh - 320px)", overflowY: "auto" }}>
              <Table
                size="small"
                stickyHeader
                sx={{ tableLayout: "fixed", width: "100%" }}
              >
                <TableHead>
                  <TableRow>
                    {headers[viewMode].map((h) => (
                      <TableCell
                        key={h.label}
                        sx={{
                          width: h.w,
                          background: BRAND,
                          color: "#fff",
                          fontSize: 11,
                          fontWeight: 600,
                          py: "8px",
                          px: "10px",
                          borderBottom: "none",
                          textAlign: h.align,
                          position: "sticky",
                          top: 0,
                          zIndex: 10,
                        }}
                      >
                        {h.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {currentData.length > 0 ? (
                    currentData.map((row, i) => (
                      <TableRow
                        key={i}
                        sx={{
                          cursor: "pointer",
                          "&:nth-of-type(even) td": { background: "#f8faff" },
                          "&:hover td": { background: "rgba(21,76,121,0.04)" },
                          "&:last-child td": { borderBottom: "none" },
                        }}
                      >
                        {viewMode === "allocation" && (
                          <>
                            <TableCell
                              sx={{
                                fontSize: 12, px: "10px", py: "8px",
                                whiteSpace: "normal", wordBreak: "break-word",
                                lineHeight: 1.4,
                              }}
                            >
                              {row.CDLjob}
                            </TableCell>
                            <TableCell sx={{ textAlign: "center", px: "6px", py: "8px" }}>
                              <Box
                                component="span"
                                sx={{
                                  display: "inline-block", px: "8px", py: "2px",
                                  borderRadius: "10px", fontSize: 11, fontWeight: 600,
                                  background: "#e8f0fe", color: BRAND,
                                }}
                              >
                                {row.countIWOno}
                              </Box>
                            </TableCell>
                          </>
                        )}

                        {viewMode === "supervisor" && (
                          <>
                            <TableCell
                              sx={{
                                fontSize: 12, px: "10px", py: "8px",
                                whiteSpace: "normal", wordBreak: "break-word",
                              }}
                            >
                              <Box
                                component="span"
                                sx={{
                                  display: "inline-block", px: "7px", py: "1px",
                                  borderRadius: "8px", fontSize: 11, fontWeight: 600,
                                  background: "#e8f0fe", color: BRAND,
                                }}
                              >
                                {row.LocCode}
                              </Box>
                            </TableCell>
                            <TableCell
                              sx={{
                                fontSize: 12, px: "10px", py: "8px",
                                whiteSpace: "normal", wordBreak: "break-word",
                                fontWeight: 500,
                              }}
                            >
                              {row.Supervisorname}
                            </TableCell>
                            <TableCell sx={{ textAlign: "center", px: "6px", py: "8px" }}>
                              <Box
                                component="span"
                                sx={{
                                  display: "inline-block", px: "8px", py: "2px",
                                  borderRadius: "10px", fontSize: 11, fontWeight: 600,
                                  background: "#e8f0fe", color: BRAND,
                                }}
                              >
                                {row.countIWOno}
                              </Box>
                            </TableCell>
                          </>
                        )}

                        {viewMode === "details" && (
                          <>
                            <TableCell
                              sx={{
                                fontSize: 12, px: "10px", py: "8px",
                                whiteSpace: "normal", wordBreak: "break-word",
                                lineHeight: 1.4,
                              }}
                            >
                              {row.EngSpec}
                            </TableCell>
                            <TableCell sx={{ textAlign: "center", px: "6px", py: "8px" }}>
                              <Box
                                component="span"
                                sx={{
                                  display: "inline-block", px: "8px", py: "2px",
                                  borderRadius: "10px", fontSize: 11, fontWeight: 600,
                                  background: "#e8f0fe", color: BRAND,
                                }}
                              >
                                {row.countIWOno}
                              </Box>
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={colCount}
                        sx={{ textAlign: "center", py: 6, fontSize: 13, color: "#94a3b8" }}
                      >
                        No records found
                      </TableCell>
                    </TableRow>
                  )}

                  {/* Total row */}
                  {currentData.length > 0 && (
                    <TableRow sx={{ "& td": { background: "#EFF4FF !important", borderBottom: "none" } }}>
                      {viewMode === "allocation" && (
                        <>
                          <TableCell sx={{ fontSize: 12, fontWeight: 600, color: BRAND, px: "10px", py: "8px" }}>
                            Total
                          </TableCell>
                          <TableCell sx={{ textAlign: "center", px: "6px", py: "8px" }}>
                            <Box
                              component="span"
                              sx={{
                                display: "inline-block", px: "8px", py: "2px",
                                borderRadius: "10px", fontSize: 11, fontWeight: 600,
                                background: "#fff", color: BRAND, border: `0.5px solid #c7d7f8`,
                              }}
                            >
                              {totalEmployees}
                            </Box>
                          </TableCell>
                        </>
                      )}
                      {viewMode === "supervisor" && (
                        <>
                          <TableCell sx={{ fontSize: 12, fontWeight: 600, color: BRAND, px: "10px", py: "8px" }}>
                            Total
                          </TableCell>
                          <TableCell sx={{ px: "10px", py: "8px" }} />
                          <TableCell sx={{ textAlign: "center", px: "6px", py: "8px" }}>
                            <Box
                              component="span"
                              sx={{
                                display: "inline-block", px: "8px", py: "2px",
                                borderRadius: "10px", fontSize: 11, fontWeight: 600,
                                background: "#fff", color: BRAND, border: `0.5px solid #c7d7f8`,
                              }}
                            >
                              {totalEmployees}
                            </Box>
                          </TableCell>
                        </>
                      )}
                      {viewMode === "details" && (
                        <>
                          <TableCell sx={{ fontSize: 12, fontWeight: 600, color: BRAND, px: "10px", py: "8px" }}>
                            Total
                          </TableCell>
                          <TableCell sx={{ textAlign: "center", px: "6px", py: "8px" }}>
                            <Box
                              component="span"
                              sx={{
                                display: "inline-block", px: "8px", py: "2px",
                                borderRadius: "10px", fontSize: 11, fontWeight: 600,
                                background: "#fff", color: BRAND, border: `0.5px solid #c7d7f8`,
                              }}
                            >
                              {totalEmployees}
                            </Box>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        )}
      </Page>
    </LocalizationProvider>
  );
}